import { useState, useEffect } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Key, Eye, EyeOff, Loader2, CheckCircle2, XCircle, AlertCircle, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

// OPARA v7.0 - Sacred Domain Detection
import { BACKEND_URL } from "@/utils/apiConfig";
import { useTranslation } from "@/hooks/useTranslation";
import { getReadableErrorMessage } from "@/utils/errorHandler";

function ResetPasswordPage() {
  const { t, locale } = useTranslation();
  const { token } = useParams();
  const navigate = useNavigate();
  const loginPath = locale === 'tr' ? '/tr/giris' : `/${locale}/login`;
  
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(true);
  const [tokenValid, setTokenValid] = useState(false);
  const [userEmail, setUserEmail] = useState("");
  const [resetSuccess, setResetSuccess] = useState(false);

  // Password validation states
  const [passwordChecks, setPasswordChecks] = useState({
    minLength: false,
    hasUppercase: false,
    hasLowercase: false,
    hasNumber: false,
    hasSpecial: false
  });

  // Verify token on mount
  useEffect(() => {
    verifyToken();
  }, [token]);

  // Update password checks as user types
  useEffect(() => {
    setPasswordChecks({
      minLength: newPassword.length >= 8,
      hasUppercase: /[A-Z]/.test(newPassword),
      hasLowercase: /[a-z]/.test(newPassword),
      hasNumber: /[0-9]/.test(newPassword),
      hasSpecial: /[!@#$%^&*()_+\-=\[\]{}|;:,.<>?\/~`]/.test(newPassword)
    });
  }, [newPassword]);

  const verifyToken = async () => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/auth/verify-reset-token/${token}`
      );

      if (response.data.valid) {
        setTokenValid(true);
        setUserEmail(response.data.email);
      } else {
        setTokenValid(false);
        toast.error(getReadableErrorMessage({ response: { data: { code: response.data.code } } }, t, 'auth.reset.token_invalid_toast'));
      }
    } catch (error) {
      console.error("Token verification error:", error);
      setTokenValid(false);
      toast.error(t('auth.reset.token_verify_failed'));
    } finally {
      setVerifying(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validation
    if (!newPassword || !confirmPassword) {
      toast.error(t('auth.reset.validation_all_fields'));
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error(t('auth.reset.validation_mismatch'));
      return;
    }

    const allChecksPassed = Object.values(passwordChecks).every(check => check);
    if (!allChecksPassed) {
      toast.error(t('auth.reset.validation_requirements'));
      return;
    }

    setLoading(true);

    try {
      const response = await axios.post(
        `${BACKEND_URL}/api/auth/reset-password`,
        {
          token: token,
          new_password: newPassword
        },
        {
          headers: {
            "Content-Type": "application/json"
          }
        }
      );

      setResetSuccess(true);
      toast.success(t('auth.reset.success_toast'));

      // Redirect to login after 3 seconds
      setTimeout(() => {
        navigate(loginPath);
      }, 3000);

    } catch (error) {
      console.error("Password reset error:", error);
      toast.error(getReadableErrorMessage(error, t, 'auth.generic_error'));
    } finally {
      setLoading(false);
    }
  };

  if (verifying) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-12 h-12 text-[#FFDD59] animate-spin mx-auto mb-4" />
          <p className="text-gray-300">{t('auth.reset.verifying')}</p>
        </div>
      </div>
    );
  }

  if (!tokenValid) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
        <Card className="bg-gray-900 border-2 border-red-500 max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <XCircle className="w-16 h-16 text-red-500" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              {t('auth.reset.invalid_title')}
            </CardTitle>
            <CardDescription className="text-gray-300 mt-2">
              {t('auth.reset.invalid_desc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to="/sifremi-unuttum">
              <Button className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold">
                {t('auth.reset.request_new')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (resetSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
        <Card className="bg-gray-900 border-2 border-green-500 max-w-md w-full">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <CheckCircle2 className="w-16 h-16 text-green-500" />
            </div>
            <CardTitle className="text-2xl font-bold text-white">
              {t('auth.reset.success_title')}
            </CardTitle>
            <CardDescription className="text-gray-300 mt-2">
              {t('auth.reset.success_desc')}
            </CardDescription>
          </CardHeader>
          <CardContent>
            <Link to={loginPath}>
              <Button className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold">
                {t('auth.login.submit_btn')}
              </Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-black via-gray-900 to-black flex items-center justify-center p-4">
      <div className="w-full max-w-md">
        {/* Back to Login Button */}
        <Link to={loginPath}>
          <Button
            variant="outline"
            className="mb-6 bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-semibold border-0"
          >
            <ArrowLeft className="w-5 h-5 mr-2" />
            {t('auth.back_to_login')}
          </Button>
        </Link>
        
        <Card className="bg-gray-900 border-2 border-[#FFDD59]">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 rounded-full bg-gray-800 flex items-center justify-center">
                <Key className="w-8 h-8 text-[#FFDD59]" />
              </div>
            </div>
            
            <CardTitle className="text-2xl font-bold text-[#FFDD59]">
              {t('auth.reset.title')}
            </CardTitle>

            <CardDescription className="text-gray-300 mt-2">
              {t('auth.reset.desc', { email: userEmail }).split('||').map((seg, i) => (
                i % 2 === 1 ? <strong key={i}>{seg}</strong> : seg
              ))}
            </CardDescription>
          </CardHeader>

          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* New Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.reset.new_password')}
                </label>
                <div className="relative">
                  <Input
                    type={showPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-gray-800 border-gray-700 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Confirm Password */}
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">
                  {t('auth.reset.confirm_password')}
                </label>
                <div className="relative">
                  <Input
                    type={showConfirmPassword ? "text" : "password"}
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    placeholder="••••••••"
                    required
                    className="bg-gray-800 border-gray-700 text-white pr-10"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
                  >
                    {showConfirmPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                  </button>
                </div>
              </div>

              {/* Password Requirements */}
              <div className="bg-gray-800/50 rounded-lg p-4 space-y-2">
                <p className="text-sm font-medium text-gray-300 mb-2 flex items-center gap-2">
                  <AlertCircle className="w-4 h-4" />
                  {t('auth.reset.requirements_title')}
                </p>
                <div className="space-y-1">
                  <PasswordCheck met={passwordChecks.minLength} text={t('auth.reset.req_minlength')} />
                  <PasswordCheck met={passwordChecks.hasUppercase} text={t('auth.reset.req_uppercase')} />
                  <PasswordCheck met={passwordChecks.hasLowercase} text={t('auth.reset.req_lowercase')} />
                  <PasswordCheck met={passwordChecks.hasNumber} text={t('auth.reset.req_number')} />
                  <PasswordCheck met={passwordChecks.hasSpecial} text={t('auth.password.special')} />
                </div>
              </div>

              {/* Password Match Indicator */}
              {confirmPassword && (
                <div className={`text-sm ${
                  newPassword === confirmPassword 
                    ? "text-green-500" 
                    : "text-red-500"
                }`}>
                  {newPassword === confirmPassword
                    ? t('auth.reset.match_yes')
                    : t('auth.reset.match_no')
                  }
                </div>
              )}

              {/* Submit Button */}
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-[#FFDD59] hover:bg-[#E6C84D] text-black font-bold text-base py-6 mt-4"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                    {t('auth.reset.updating')}
                  </>
                ) : (
                  t('auth.reset.update_btn')
                )}
              </Button>
            </form>
          </CardContent>
        </Card>

        {/* Help Text */}
        <div className="mt-6 text-center text-sm text-gray-400">
          <Link to={loginPath} className="text-[#FFDD59] hover:text-[#E6C84D]">
            {t('auth.back_to_login')}
          </Link>
        </div>
      </div>
    </div>
  );
}

// Password Check Component
function PasswordCheck({ met, text }) {
  return (
    <div className="flex items-center gap-2 text-sm">
      {met ? (
        <CheckCircle2 className="w-4 h-4 text-green-500" />
      ) : (
        <XCircle className="w-4 h-4 text-gray-500" />
      )}
      <span className={met ? "text-green-500" : "text-gray-400"}>
        {text}
      </span>
    </div>
  );
}

export default ResetPasswordPage;
